// In-store ticket/token number display resolution (store-ticket-number spec).
//
// Mirrors admin-mobile-app/src/utils/storeTicket.js. Keep the two byte-identical apart
// from the one header line naming the mirror direction, following the existing
// money.js / partyLabel.js / tenantTime.js duplication convention.
//
// WHY THIS FILE EXISTS. The ticket number has to appear on four independent surfaces --
// the POS queue row, the kitchen ticket, the itemized bill, and the Take Order send
// confirmation -- and two of those (the print builders) are separate hand-maintained
// copies inside two ~5,000-line files. Resolving the number in one pure place is what
// makes "the queue and the chit show the same number" true by construction rather than
// by vigilance.
//
// THE SNAPSHOT FALLBACK IS NOT OPTIONAL. `create_pos_order` stamps `storeTicketNumber`
// on the order AND mirrors it into `customerSubmittedSnapshot`, exactly as it does for
// `tableLabel` and `partyLabel`, because every POS read surface in this codebase reads
// `order.X || snapshot.X`. Reading only the top-level field would work for freshly
// created orders and quietly render blank for any record that reached a surface through
// a path that carries the snapshot -- a bug that looks like a UI fault and is actually a
// missing fallback.
//
// Pure: no React, no native imports, no i18n. Callers choose their own label, because
// the Take Order screen is translated and the print builders are English by owner
// request.

/**
 * readStoreTicketNumber(order) -> '001' | ''
 *
 * The tenant-facing token if this order carries one, otherwise ''. Empty means "this
 * order has no ticket number" -- either the tenant has the feature off, the order
 * predates it, or the counter failed open at create time. All three are the same thing
 * to a display surface: fall back to the order number.
 *
 * Total function. Never throws on a malformed order.
 */
export function readStoreTicketNumber(order) {
  if (!order || typeof order !== 'object') return '';
  const direct = order.storeTicketNumber;
  if (typeof direct === 'string' && direct.trim()) return direct.trim();
  // Numbers are accepted because a hand-written or re-serialized record could carry one;
  // the backend always writes a pre-formatted string.
  if (typeof direct === 'number' && Number.isFinite(direct)) return String(direct);

  const snap = order.customerSubmittedSnapshot;
  if (!snap || typeof snap !== 'object') return '';
  const mirrored = snap.storeTicketNumber;
  if (typeof mirrored === 'string' && mirrored.trim()) return mirrored.trim();
  if (typeof mirrored === 'number' && Number.isFinite(mirrored)) return String(mirrored);
  return '';
}

/**
 * hasStoreTicketNumber(order) -> boolean
 *
 * For call sites that need to switch a LABEL as well as a value ("Ticket Number" vs
 * "Order Number"), so they do not have to compare the resolved value against the order
 * number to work out which one they got.
 */
export function hasStoreTicketNumber(order) {
  return readStoreTicketNumber(order) !== '';
}

/**
 * orderDisplayNumber(order) -> the single number to show a human
 *
 * The ticket number when there is one, the order number otherwise. This is the
 * "instead of the existing order number" rule from the requirement, expressed once.
 *
 * NOTE it is a DISPLAY value only. `order_number` remains the identifier for every
 * lookup, mutation and payment reference -- it is the orders table partition key and is
 * embedded in the PhonePe merchantOrderId. Never send this value to an API.
 */
export function orderDisplayNumber(order) {
  const ticket = readStoreTicketNumber(order);
  if (ticket) return ticket;
  if (!order || typeof order !== 'object') return '';
  const num = order.order_number;
  return typeof num === 'string' || typeof num === 'number' ? String(num).trim() : '';
}
